<template>
    <div class="prompts-view-wrap">
        <v-row dense class="mb-2">
            <v-col cols="12" md="6">
                <v-card class="prompt-metric-card" outlined>
                    <div class="prompt-metric-card__top">
                        <div>
                            <div class="prompt-metric-card__label">Tracked Prompts</div>
                            <div class="prompt-metric-card__value">{{ trackedPromptsCount }}</div>
                        </div>
                        <v-btn small rounded outlined color="primary" @click="$emit('openAddPrompts')">
                            <v-icon x-small class="mr-1">mdi-plus</v-icon>
                            Add Prompts
                        </v-btn>
                    </div>
                </v-card>
            </v-col>
            <v-col cols="12" md="6">
                <v-card class="prompt-metric-card" outlined>
                    <div class="prompt-metric-card__top mb-2">
                        <div class="prompt-metric-card__label">Tracked Platforms</div>
                        <v-btn small rounded outlined color="primary" @click="$emit('openProjectSettings')">
                            <v-icon x-small class="mr-1">mdi-cog</v-icon>
                            Add Platforms
                        </v-btn>
                    </div>
                    <div class="prompt-metric-platforms">
                        <v-chip v-for="platform in trackedPlatforms" :key="platform.key" small outlined class="mr-1 mb-1 prompt-platform-chip">
                            <span v-if="platform.logo" class="prompt-platform-chip__logo" v-html="platform.logo"></span>
                            {{ platform.label }}
                        </v-chip>
                        <span v-if="!trackedPlatforms.length" class="prompt-metric-platforms__empty">No platforms configured</span>
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <v-card class="rounded_card my-5 quaroshadow" style="border-top: 4px solid var(--v-primary-base); overflow: hidden">
            <v-toolbar id="projects-tool-bar" flat class="gradient-toolbar elevation-0">
                <v-toolbar-items class="prompt-toolbarbtns mr-3">
                    <v-btn text class="active-button prompt-toolbar-btn" color="primary darken-1">
                        <h2 class="prompt-toolbar-btn__title">All Prompts</h2>
                        <v-tooltip top max-width="320" color="primary">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on" small class="ml-1" color="grey" style="vertical-align: middle">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>All tracked prompts in the selected filter context.</span>
                        </v-tooltip>
                    </v-btn>
                </v-toolbar-items>
                <div class="flex-grow-1"></div>
                <v-text-field
                    class="pr-5"
                    v-model="localSearch"
                    rounded
                    dense
                    filled
                    clearable
                    clear-icon="mdi-close"
                    prepend-inner-icon="mdi-magnify"
                    label="Search..."
                    single-line
                    hide-details
                    aria-label="Search table"
                    @input="onSearchInput" />
                <v-btn rounded color="primary" filled @click="exportLocalTable" aria-label="Export table">
                    <v-icon aria-hidden="true">mdi-cloud-download</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text class="px-0">
                <PromptsTable
                    ref="table"
                    :isActive="true"
                    :brands="brands"
                    :sentimentAvailable="sentimentAvailable"
                    :hasTags="hasTags"
                    :initialFilters="initialFilters"
                    :initialFiltersReady="initialFiltersReady"
                    :tags="tags"
                    :availableTags="availableTags"
                    :tagsOperator="tagsOperator"
                    :selectedPlatform="selectedPlatform"
                    :search="localSearch"
                    :platforms="platforms"
                    :brand="brand"
                    :project="project"
                    :responses="responses"
                    :loading="loading"
                    :availableDates="availableDates"
                    :selectedDates="selectedDates"
                    @tagsUpdated="$emit('tagsUpdated', $event)"
                    @filtersUpdated="$emit('filtersUpdated', $event)" />
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
    import PromptsTable from "../../../components/aisuite/PromptsTable.vue"

    export default {
        name: "AIProjectPromptsView",
        components: { PromptsTable },
        props: {
            brands: { type: Array, default: () => [] },
            sentimentAvailable: { type: Boolean, default: false },
            hasTags: { type: Boolean, default: false },
            initialFilters: { type: Array, default: () => [] },
            initialFiltersReady: { type: Boolean, default: false },
            tags: { type: Array, default: () => [] },
            availableTags: { type: Array, default: () => [] },
            tagsOperator: { type: String, default: "or" },
            selectedPlatform: { type: [Object, String], default: null },
            search: { type: String, default: "" },
            platforms: { type: Array, default: () => [] },
            platformItems: { type: Array, default: () => [] },
            brand: { type: Object, default: () => ({}) },
            project: { type: Object, default: null },
            responses: { type: Array, default: () => [] },
            loading: { type: Boolean, default: false },
            availableDates: { type: Array, default: () => [] },
            selectedDates: { type: Array, default: () => [] }
        },
        data() {
            return {
                localSearch: ""
            }
        },
        computed: {
            trackedPromptsCount() {
                const projectCount = Number(this.project?.prompt_count)
                if (Number.isFinite(projectCount) && projectCount >= 0) return projectCount

                const fallbackCount = Number(this.project?.keyword_count)
                if (Number.isFinite(fallbackCount) && fallbackCount >= 0) return fallbackCount

                return 0
            },
            trackedPlatforms() {
                const labelMap = {
                    chatgpt: "ChatGPT",
                    gemini: "Gemini",
                    google: "Google AIO",
                    google_ai_mode: "AI Mode",
                    perplexity: "Perplexity",
                    grok: "Grok",
                    openai: "OpenAI",
                    claude: "Claude"
                }

                const rawPlatforms = Array.isArray(this.platforms) ? this.platforms : []
                const platformMeta = Array.isArray(this.platformItems) ? this.platformItems : []
                const metaByKey = new Map(
                    platformMeta.filter((item) => item && item.key).map((item) => [String(item.key).toLowerCase(), { label: item.label || item.name || item.key, logo: item.logo || null }])
                )

                const keys = rawPlatforms.map((p) => (p || "").toString().trim().toLowerCase()).filter(Boolean)
                if (this.project?.google) keys.push("google")

                const uniq = Array.from(new Set(keys))
                return uniq.map((key) => {
                    const meta = metaByKey.get(key) || {}
                    return {
                        key,
                        label: meta.label || labelMap[key] || key,
                        logo: meta.logo || null
                    }
                })
            }
        },
        watch: {
            search: {
                immediate: true,
                handler(val) {
                    this.localSearch = val || ""
                }
            }
        },
        methods: {
            onSearchInput() {
                this.$emit("update:search", this.localSearch)
            },
            async exportLocalTable() {
                const csv = await this.exportTableData()
                if (!csv || typeof csv !== "string") return
                const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
                const link = document.createElement("a")
                link.href = URL.createObjectURL(blob)
                link.download = `prompts_export_${new Date().toISOString().slice(0, 10)}.csv`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(link.href)
            },
            async exportTableData() {
                const table = this.$refs.table
                if (table && typeof table.exportTableData === "function") {
                    return table.exportTableData("dialog")
                }
                return ""
            }
        }
    }
</script>

<style scoped>
    .prompt-metric-card {
        border-radius: 12px;
        border-color: rgba(15, 23, 42, 0.1) !important;
        background: rgba(255, 255, 255, 0.72);
        padding: 12px;
        min-height: 110px;
    }
    .prompt-metric-card__top {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
    }
    .prompt-metric-card__label {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.35px;
        color: rgba(55, 65, 81, 0.82);
        margin-bottom: 4px;
    }
    .prompt-metric-card__value {
        font-size: 30px;
        font-weight: 700;
        line-height: 1.1;
        color: #1f2937;
    }
    .prompt-metric-platforms {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
    }
    .prompt-metric-platforms__empty {
        font-size: 12px;
        color: rgba(75, 85, 99, 0.74);
    }
    .prompt-platform-chip__logo {
        width: 14px;
        height: 14px;
        margin-right: 6px;
        display: inline-flex;
        align-items: center;
    }
    .prompt-platform-chip__logo :deep(svg) {
        width: 14px;
        height: 14px;
        display: block;
    }
    .prompt-toolbarbtns .v-btn {
        text-transform: none !important;
        letter-spacing: 0 !important;
        font-weight: 600;
        border-radius: 10px !important;
        margin: 4px 2px;
    }
    .prompt-toolbar-btn {
        text-transform: none !important;
        letter-spacing: 0 !important;
    }
    .prompt-toolbar-btn__title {
        margin: 0;
        font-size: 0.9rem;
        font-weight: 100;
        text-transform: none !important;
        letter-spacing: 0 !important;
    }
    .theme--dark .prompt-metric-card {
        background: rgba(255, 255, 255, 0.04);
        border-color: rgba(148, 163, 184, 0.24) !important;
    }
    .theme--dark .prompt-metric-card__label {
        color: rgba(203, 213, 225, 0.84);
    }
    .theme--dark .prompt-metric-card__value {
        color: #f3f6fb;
    }
    .theme--dark .prompt-metric-platforms__empty {
        color: rgba(203, 213, 225, 0.72);
    }
</style>
